/**
 * GPU occupancy lookup: open-addressed hash of packed (i,j,k, oct16) voxels.
 *
 * The vertex shader used to binary-search the sorted member/shell lists on
 * every point of every visible node. That is O(log N) texel fetches per
 * surface (~40 for a 500k-voxel floor) and dominates camera rotation once
 * several surfaces are highlighted. This table is built once on grow and
 * looked up with a few probes (same hash in occupancyHash.vs helpers).
 *
 * Layout in the occupancy texture: member, shell, frontier, then in-plane
 * snap halo (power-of-two slots each). Empty slots are i=j=k=0xFFFF.
 * Must stay in lockstep with `surfaceHashIjk` / `surfaceHashHas` in
 * pointcloud.vs.
 */

export const HASH_EMPTY = 0xffff;
export const HASH_MAX_PROBE = 32;
export const OCC_TEX_WIDTH = 2048;
/** Must match `SURFACE_SNAP_CHEB` in pointcloud.vs. */
export const SNAP_CHEB = 2;
const GOLDEN_X = 73856093;
const GOLDEN_Y = 19349663;
const GOLDEN_Z = 83492791;

export function hashIjk(i, j, k) {
  const u = (n) => n & 65535;
  return (
    (Math.imul(u(i), GOLDEN_X) ^
      Math.imul(u(j), GOLDEN_Y) ^
      Math.imul(u(k), GOLDEN_Z)) >>>
    0
  );
}

/** Odd stride so double hashing visits every slot of a power-of-two table. */
export function hashStride(h) {
  return ((h >>> 16) | 1) >>> 0;
}

function nextPow2(n) {
  let s = 1;
  while (s < n) s <<= 1;
  return s;
}

export function readPackedVoxel(raw, index) {
  const o = index * 8;
  if (o + 8 > raw.length) {
    return { i: HASH_EMPTY, j: HASH_EMPTY, k: HASH_EMPTY, oct: 0 };
  }
  return {
    i: raw[o] | (raw[o + 1] << 8),
    j: raw[o + 2] | (raw[o + 3] << 8),
    k: raw[o + 4] | (raw[o + 5] << 8),
    oct: raw[o + 6] | (raw[o + 7] << 8),
  };
}

function writePackedVoxel(table, slot, v) {
  const o = slot * 8;
  table[o] = v.i & 255;
  table[o + 1] = (v.i >> 8) & 255;
  table[o + 2] = v.j & 255;
  table[o + 3] = (v.j >> 8) & 255;
  table[o + 4] = v.k & 255;
  table[o + 5] = (v.k >> 8) & 255;
  table[o + 6] = v.oct & 255;
  table[o + 7] = (v.oct >> 8) & 255;
}

function slotEmpty(table, slot) {
  const o = slot * 8;
  return (
    table[o] === 255 &&
    table[o + 1] === 255 &&
    table[o + 2] === 255 &&
    table[o + 3] === 255 &&
    table[o + 4] === 255 &&
    table[o + 5] === 255
  );
}

function buildTable(voxels) {
  if (voxels.length === 0) {
    return { table: new Uint8Array(0), size: 0 };
  }
  let size = nextPow2(Math.max(4, voxels.length * 2));
  for (;;) {
    const table = new Uint8Array(size * 8).fill(255);
    const mask = size - 1;
    let ok = true;
    for (let n = 0; n < voxels.length; n++) {
      const v = voxels[n];
      const h = hashIjk(v.i, v.j, v.k);
      const stride = hashStride(h);
      const wi = v.i & 65535;
      const wj = v.j & 65535;
      const wk = v.k & 65535;
      let placed = false;
      for (let probe = 0; probe < HASH_MAX_PROBE; probe++) {
        const slot = (h + Math.imul(probe, stride)) & mask;
        if (slotEmpty(table, slot)) {
          writePackedVoxel(table, slot, v);
          placed = true;
          break;
        }
        const existing = readPackedVoxel(table, slot);
        if (existing.i === wi && existing.j === wj && existing.k === wk) {
          placed = true;
          break;
        }
      }
      if (!placed) {
        ok = false;
        break;
      }
    }
    if (ok) return { table, size };
    if (size >= 1 << 24) {
      throw new Error('occupancy hash table exceeded 16M slots');
    }
    size *= 2;
  }
}

export function hashLookup(table, size, i, j, k) {
  if (size <= 0 || !table || table.length < 8) return null;
  const h = hashIjk(i, j, k);
  const stride = hashStride(h);
  const mask = size - 1;
  const wi = i & 65535;
  const wj = j & 65535;
  const wk = k & 65535;
  for (let probe = 0; probe < HASH_MAX_PROBE; probe++) {
    const slot = (h + Math.imul(probe, stride)) & mask;
    const v = readPackedVoxel(table, slot);
    if (v.i === HASH_EMPTY && v.j === HASH_EMPTY && v.k === HASH_EMPTY) {
      return null;
    }
    if (v.i === wi && v.j === wj && v.k === wk) return v;
  }
  return null;
}

function buildTableFromPacked(raw, start, count) {
  if (count <= 0) {
    return { table: new Uint8Array(0), size: 0 };
  }
  let size = nextPow2(Math.max(4, count * 2));
  for (;;) {
    const table = new Uint8Array(size * 8).fill(255);
    const mask = size - 1;
    let ok = true;
    for (let n = 0; n < count; n++) {
      const v = readPackedVoxel(raw, start + n);
      if (v.i === HASH_EMPTY && v.j === HASH_EMPTY && v.k === HASH_EMPTY) continue;
      const h = hashIjk(v.i, v.j, v.k);
      const stride = hashStride(h);
      const wi = v.i & 65535;
      const wj = v.j & 65535;
      const wk = v.k & 65535;
      let placed = false;
      for (let probe = 0; probe < HASH_MAX_PROBE; probe++) {
        const slot = (h + Math.imul(probe, stride)) & mask;
        if (slotEmpty(table, slot)) {
          writePackedVoxel(table, slot, v);
          placed = true;
          break;
        }
        const existing = readPackedVoxel(table, slot);
        if (existing.i === wi && existing.j === wj && existing.k === wk) {
          placed = true;
          break;
        }
      }
      if (!placed) {
        ok = false;
        break;
      }
    }
    if (ok) return { table, size };
    if (size >= 1 << 24) {
      throw new Error('occupancy hash table exceeded 16M slots');
    }
    size *= 2;
  }
}

function stepInPlane(di, dj, dk, planeN) {
  const len = Math.hypot(di, dj, dk);
  if (len < 0.5) return false;
  return Math.abs((di * planeN[0] + dj * planeN[1] + dk * planeN[2]) / len) <= 0.5;
}

/** Pack u16 ijk into a float64 key (fits in the 53-bit integer range). */
function ijkKey(i, j, k) {
  return (i * 65536 + j) * 65536 + k;
}

function inPlaneOffsets(planeN) {
  const offsets = [];
  for (let di = -SNAP_CHEB; di <= SNAP_CHEB; di++) {
    for (let dj = -SNAP_CHEB; dj <= SNAP_CHEB; dj++) {
      for (let dk = -SNAP_CHEB; dk <= SNAP_CHEB; dk++) {
        if (di === 0 && dj === 0 && dk === 0) continue;
        if (stepInPlane(di, dj, dk, planeN)) offsets.push(di, dj, dk);
      }
    }
  }
  return offsets;
}

/**
 * Empty / LOD cells that the vertex shader used to find with a 5³ hash walk.
 * Baking them here keeps highlight stable while the camera moves.
 *
 * Fallback only, for backends that do not pack a snap halo (`snapCount`
 * absent). It has no occupancy and so no notion of a gap: it covers any cell
 * within reach, which is how a pipe one empty cell off a wall used to light
 * up. Cells that already carry packed occupancy (shell, frontier) are skipped
 * — the backend has judged those.
 */
export function collectSnapHalo(raw, memberCount, planeNormal, packedCount = memberCount) {
  const nMem = Math.max(0, memberCount | 0);
  if (nMem <= 0 || !planeNormal) return [];
  const len = Math.hypot(planeNormal[0], planeNormal[1], planeNormal[2]);
  if (!(len > 1e-8)) return [];
  const pn = [planeNormal[0] / len, planeNormal[1] / len, planeNormal[2] / len];
  const offsets = inPlaneOffsets(pn);
  const members = new Set();
  const list = [];
  for (let n = 0; n < nMem; n++) {
    const v = readPackedVoxel(raw, n);
    if (v.i === HASH_EMPTY && v.j === HASH_EMPTY && v.k === HASH_EMPTY) continue;
    members.add(ijkKey(v.i, v.j, v.k));
    list.push(v);
  }
  const occupied = new Set(members);
  for (let n = nMem; n < Math.max(nMem, packedCount | 0); n++) {
    const v = readPackedVoxel(raw, n);
    if (v.i === HASH_EMPTY && v.j === HASH_EMPTY && v.k === HASH_EMPTY) continue;
    occupied.add(ijkKey(v.i, v.j, v.k));
  }
  const halo = [];
  const seen = new Set();
  for (let n = 0; n < list.length; n++) {
    const v = list[n];
    for (let o = 0; o < offsets.length; o += 3) {
      const i = v.i + offsets[o];
      const j = v.j + offsets[o + 1];
      const k = v.k + offsets[o + 2];
      if (i < 0 || j < 0 || k < 0 || i > 65535 || j > 65535 || k > 65535) continue;
      const key = ijkKey(i, j, k);
      if (occupied.has(key) || seen.has(key)) continue;
      seen.add(key);
      halo.push({ i, j, k, oct: v.oct });
    }
  }
  return halo;
}

function aabbOfPacked(raw, start, count) {
  let minI = Infinity;
  let minJ = Infinity;
  let minK = Infinity;
  let maxI = -Infinity;
  let maxJ = -Infinity;
  let maxK = -Infinity;
  for (let n = 0; n < count; n++) {
    const v = readPackedVoxel(raw, start + n);
    if (v.i === HASH_EMPTY && v.j === HASH_EMPTY && v.k === HASH_EMPTY) continue;
    if (v.i < minI) minI = v.i;
    if (v.j < minJ) minJ = v.j;
    if (v.k < minK) minK = v.k;
    if (v.i > maxI) maxI = v.i;
    if (v.j > maxJ) maxJ = v.j;
    if (v.k > maxK) maxK = v.k;
  }
  if (!Number.isFinite(minI)) {
    return { aabbMin: [0, 0, 0], aabbMax: [0, 0, 0] };
  }
  return { aabbMin: [minI, minJ, minK], aabbMax: [maxI, maxJ, maxK] };
}

/**
 * Convert backend sorted occupancy (members, shell, frontier, then the snap halo when
 * the backend packs one) into a GPU hash texture payload.
 * @param {Uint8Array} raw
 * @param {number} memberCount
 * @param {number} [blockCount]
 * @param {number} [frontierCount]
 */
export function packOccupancyHash(
  raw,
  memberCount,
  blockCount = 0,
  frontierCount = 0,
  planeNormal = null,
  snapCount = 0
) {
  const nMem = Math.max(0, memberCount | 0);
  const nBlk = Math.max(0, blockCount | 0);
  const nFront = Math.max(0, frontierCount | 0);
  const { aabbMin, aabbMax } = aabbOfPacked(raw, 0, nMem);
  const memberTable = buildTableFromPacked(raw, 0, nMem);
  const shellTable = buildTableFromPacked(raw, nMem, nBlk);
  const frontierTable = buildTableFromPacked(raw, nMem + nBlk, nFront);
  // The backend packs the snap halo after frontier when it can: it has the
  // occupancy, so it walks through rough sheet cells but not across a gap.
  // Fall back to the reach-only client walk for backends that do not.
  const nSnap = Math.max(0, snapCount | 0);
  const snapTable =
    nSnap > 0
      ? buildTableFromPacked(raw, nMem + nBlk + nFront, nSnap)
      : buildTable(collectSnapHalo(raw, nMem, planeNormal, nMem + nBlk + nFront));
  const totalSlots =
    memberTable.size + shellTable.size + frontierTable.size + snapTable.size;
  const data = new Uint8Array(Math.max(8, totalSlots * 8)).fill(255);
  if (memberTable.size > 0) data.set(memberTable.table, 0);
  if (shellTable.size > 0) data.set(shellTable.table, memberTable.size * 8);
  if (frontierTable.size > 0) {
    data.set(frontierTable.table, (memberTable.size + shellTable.size) * 8);
  }
  if (snapTable.size > 0) {
    data.set(
      snapTable.table,
      (memberTable.size + shellTable.size + frontierTable.size) * 8
    );
  }
  const pixelCount = Math.max(2, totalSlots * 2);
  const height = Math.max(1, Math.ceil(pixelCount / OCC_TEX_WIDTH));
  return {
    data,
    memberHashSize: memberTable.size,
    shellHashSize: shellTable.size,
    frontierHashSize: frontierTable.size,
    snapHashSize: snapTable.size,
    aabbMin,
    aabbMax,
    height,
    width: OCC_TEX_WIDTH,
  };
}

/** Sorted-list binary search — used only to profile against the hash. */
export function binarySearchPacked(raw, lo, hi, i, j, k) {
  let left = lo;
  let right = hi - 1;
  const wi = i & 65535;
  const wj = j & 65535;
  const wk = k & 65535;
  while (left <= right) {
    const mid = (left + right) >> 1;
    const v = readPackedVoxel(raw, mid);
    const c =
      v.i !== wi ? v.i - wi : v.j !== wj ? v.j - wj : v.k - wk;
    if (c === 0) return v;
    if (c < 0) left = mid + 1;
    else right = mid - 1;
  }
  return null;
}
